import { FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import WithOptionsMenu, { WithMenuOption } from "@/components/WithOptionsMenu";
import { ThreeDotsBtn, ThreeDotsBtnProps } from "@/components/cards/UserContentCard/components/UserContentCardBtns";
import Mountable from "@/components/styled/div/Mountable";
import NoteToPrint from "@/components/toPrint/NoteToPrint";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useMoveToPopupContextSelector } from "@/features/MoveToPopup/hooks/useMoveToPopupContext";
import { useContentItemsSharingPopupContextSelector } from "@/features/ShareSettings/hooks/useContentItemsSharingContext";
import { useTaggingContextSelector } from "@/features/Tagging/hooks/useTaggingContext";
import { callGetFolder } from "@/hooks/folders/graphqlUtils";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import useConfirmDialog from "@/hooks/useConfirmDialog";
import LocalKeyValueStore from "@/utils/LocalKeyValueStore";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { copyToClipboard } from "@/utils/clipboard";
import { renameKey } from "@/utils/genericUtils";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";
import { getNoteUrl } from "@/utils/url";
import { ClassPermission, ItemType, Note, UserDetails } from "@knowt/syncing/graphql/schema";
import { useBookmark } from "@knowt/syncing/hooks/bookmarks/useBookmarks";
import { useClass } from "@knowt/syncing/hooks/classes/useClass";
import { useNote } from "@knowt/syncing/hooks/notes/useNotes";
import { deleteNotes, duplicateNote, moveNotes, restoreNotes, trashNotes } from "@knowt/syncing/hooks/notes/utils";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useRecentlyViewed } from "@knowt/syncing/hooks/views/useRecentlyViewed";
import { UNTITLED } from "@knowt/syncing/utils/dataCleaning";
import noop from "@knowt/syncing/utils/noop";
import { Portal } from "@mui/base/Portal";
import {
    Bookmark,
    BookmarkMinus,
    Copy,
    ExternalLink,
    FolderInput,
    KeyRound,
    Globe,
    Link,
    Lock,
    PencilLine,
    Pin,
    PinOff,
    Printer,
    Settings,
    Tag,
    Trash,
    TriangleAlert,
    Undo,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useParams, usePathname } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";

const CopilotSettingsPopup = dynamic(() => import("@/components/Popup/CopilotSettingsPopup"));
const NoteNamingPopup = dynamic(() => import("@/components/Popup/NoteNamingPopup"));

type NoteOptionsMenuProps = {
    note: Note;
    btnSx?: ThreeDotsBtnProps["sx"];
    isNotePageMenu?: boolean;
};

const NoteOptionsMenu = ({ note: initialNote, btnSx, isNotePageMenu = false }: NoteOptionsMenuProps) => {
    const router = useRouter();
    const pathname = usePathname();

    const { smDown } = useBreakPoints();

    const [shouldHookLoad, setShouldHookLoad] = useState(false);

    const { folderId } = useParams() as { folderId: string };
    const { noteId } = initialNote;
    const { remove } = useRecentlyViewed();
    const { note, update } = useNote({ noteId: noteId, isEnabled: shouldHookLoad, fallbackData: initialNote });

    const { userId, user, sharingEnabled, isStudent } = useCurrentUser();
    const { checkAuth } = useCheckAuth();

    const isPublic = note?.public;
    const isPasswordProtected = !!note?.password;
    const isClassItem = !!note?.classId;
    const readOnly = !userId || !note?.userId || note?.userId !== userId;

    const { permission } = useClass({ classId: note?.classId });

    const courseReadOnly = readOnly && permission !== ClassPermission.ALL;

    const { bookmarked, toggleBookmarked } = useBookmark({
        itemId: noteId,
        type: ItemType.NOTE,
        classId: note?.classId,
        itemUserId: note?.userId,
    });

    const openMoveToPopup = useMoveToPopupContextSelector(state => state?.openMoveToPopup);

    const { openConfirmDialog } = useConfirmDialog();

    const openTaggingPopup = useTaggingContextSelector(state => state.openTaggingPopup);

    const closeTaggingPopup = useTaggingContextSelector(state => state.closeTaggingPopup);
    const openContentItemsSharingPopup = useContentItemsSharingPopupContextSelector(
        state => state.openContentItemsSharingPopup
    );

    const [isRenamePopupOpen, setIsRenamePopupOpen] = useState(false);
    const [isCopilotSettingsOpen, setIsCopilotSettingsOpen] = useState(false);

    const printDocument = useNotePageContextSelector(state => (isNotePageMenu ? state.printDocument : null));

    const noteRoute = getNoteUrl({ noteId, title: note?.title || initialNote.title || UNTITLED });

    const handleTrashClick = async () => {
        if (note?.lock) {
            toast.error(
                "Sorry this note is locked for deleting since it's a part of our study hub, please contact us at Knowt.com/contact"
            );
            return;
        }

        if (note?.classId) {
            openConfirmDialog({
                title: "Are you sure you want to remove this note from class?",
                subtitle: "You can re-add this note to the class later if you continue.",
                style: { borderRadius: "4rem" },
                rounded: true,
                cardSize: smDown ? "31rem" : "58rem",
                spacing: 2.2,
                subtitleFontSize: "1.9rem",
                subtitleFontWeight: "500",
                buttonColor: "black",
                buttonTextColor: "white",
                simpleCancelBtn: true,
                onConfirm: async () => {
                    toast.loading("Removing note from class...", { id: "DELETE_NOTE" });
                    try {
                        await moveNotes({
                            noteIds: [{ userId, noteId }],
                            sourceFolderId: note.folderId ?? null,
                            destinationFolderId: null,
                            sourceClassId: note.classId,
                            destinationClassId: null,
                        });
                        toast.success("Note removed from class", { id: "DELETE_NOTE" });
                    } catch {
                        toast.error("Failed to remove note from class", { id: "DELETE_NOTE" });
                    }
                },
            });
        } else {
            if (isNotePageMenu) {
                return openConfirmDialog({
                    title: `Are you sure you want to delete this note?`,
                    subtitle: `You can recover this note at any point from your trash, or delete it permanently there.`,
                    onConfirm: async () => {
                        await onConfirmDeleteNote();
                    },
                    rounded: true,
                    cardSize: smDown ? "31rem" : "58rem",
                    spacing: 2.2,
                    subtitleFontSize: "1.9rem",
                    subtitleFontWeight: "500",
                    buttonColor: "black",
                    buttonTextColor: "white",
                    simpleCancelBtn: true,
                });
            }

            await trashNotes({
                noteIds: [{ userId, noteId }],
                sourceFolderId: folderId || null,
            });
            await remove([noteId]);

            toast.success("Note deleted");
        }
    };

    const deletePermanently = () => {
        if (isNotePageMenu) {
            deleteNotes({ noteIds: [{ userId, noteId: note?.noteId }], userId }).then(() => {
                toast.success("Note deleted!");
            });

            return router.back();
        }

        openConfirmDialog({
            title: "Are you sure you want to delete?",
            subtitle: "This note will not be able to be recovered if you continue to delete it.",
            style: { borderRadius: "4rem" },
            rounded: true,
            cardSize: smDown ? "31rem" : "58rem",
            spacing: 2.2,
            subtitleFontSize: "1.9rem",
            subtitleFontWeight: "500",
            buttonColor: "black",
            buttonTextColor: "white",
            simpleCancelBtn: true,
            onConfirm: async () => {
                await deleteNotes({ noteIds: [{ userId, noteId }], userId: userId as string });

                if (pathname.includes("note")) router.push("/materials");
                toast.success("Note deleted permanently");
            },
        });
    };

    const onConfirmDeleteNote = async () => {
        await toast.promise(
            trashNotes({
                noteIds: [{ userId, noteId }],
                sourceFolderId: null,
            }),
            {
                loading: "Trashing the note",
                success: "Note trashed!",
                error: "Failed to trash the note",
            }
        );

        if (pathname.includes("note")) router.push("/trash");
    };

    const setPublicAndGetLink = useCallback(
        async (newIsPublic: boolean) => {
            try {
                await update({ public: newIsPublic });
                if (newIsPublic) {
                    copyToClipboard(encodeURI(window.location.origin) + noteRoute);
                }
                toast.success(newIsPublic ? "Note link successfully copied!" : "Note sharing settings updated.");
                Mixpanel.track("Note - Shared", {
                    noteId: noteId,
                    isPublic: newIsPublic,
                    userId: user?.ID,
                    xp: user?.xp,
                    level: user?.level,
                    streak: user?.streak,
                    coins: user?.coins,
                    gamificationRecords: user?.records,
                });
            } catch {
                toast.error("Failed updating the note");
            }
        },
        [update, noteId, user?.xp, user?.ID, noteRoute]
    );

    const notAnOwnerAlert: WithMenuOption = {
        label: "Not an owner",
        hide: isStudent,
        disabled: true,
        onClick: () => noop(),
        node: (
            <FlexRowAlignCenter
                className="secondaryTextBold1"
                style={{
                    gap: "0.9rem",
                    backgroundColor: themeColors.errorSecondary,
                    padding: "1rem",
                    borderRadius: "1.2rem",
                }}>
                <TriangleAlert size={iconSizes.MD} fill={themeColors.errorPrimary} color={themeColors.errorSecondary} />{" "}
                Not an owner
            </FlexRowAlignCenter>
        ),
        menuItemProps: {
            sx: {
                padding: "0.6rem",
                cursor: "default",
                "&:hover": {
                    backgroundColor: "unset",
                },
                "&:active": {
                    backgroundColor: "unset",
                },
            },
        },
    };

    const SavedIcon = readOnly ? BookmarkMinus : PinOff;
    const UnsavedIcon = readOnly ? Bookmark : Pin;

    const saveText = bookmarked ? (readOnly ? "Unsave" : "Unpin") : readOnly ? "Save" : "Pin";

    const saveOption: WithMenuOption = {
        label: saveText,
        onClick: async () => {
            checkAuth({
                event: AuthEvent.BOOKMARK_ITEM,
                onAuthSuccess: async () => await toggleBookmarked(),
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                {bookmarked ? (
                    <SavedIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                ) : (
                    <UnsavedIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                )}
                {saveText}
            </FlexRowAlignCenter>
        ),
    };

    const fetchTheNoteFolder = async () => {
        if (!note?.folderId) return null;

        const password = (await LocalKeyValueStore.getWithExpiry(`${ItemType.FOLDER}-${note.folderId}-pwd`)) as string;
        return await callGetFolder({ folderId: note.folderId, password });
    };

    const copilotSettingsOption: WithMenuOption = {
        label: "Kai Complete Settings",
        onClick: () => {
            setIsCopilotSettingsOpen(true);
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Settings strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                Kai Complete
            </FlexRowAlignCenter>
        ),
        hide: !isNotePageMenu || readOnly,
    };

    const duplicateOption: WithMenuOption = {
        label: "Duplicate",
        onClick: async () => {
            const isAuthneticated = await checkAuth({
                event: AuthEvent.DUPLICATE_NOTE,
            });

            if (!isAuthneticated) return;

            if (note?.password) {
                return toast.error("Sorry, you cannot duplicate password-protected notes.");
            }

            const folder = await fetchTheNoteFolder();

            const newNote = await toast.promise(
                duplicateNote(noteId, user as UserDetails, {
                    public: folder?.public,
                    password: folder?.password,
                }),
                {
                    loading: "Copying to your notebook",
                    success: "Note duplicated successfully!",
                    error: "Failed to duplicate the note",
                }
            );

            router.push(getNoteUrl({ noteId: newNote.noteId, title: newNote.title ?? UNTITLED }));
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Copy strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> {"Duplicate"}
            </FlexRowAlignCenter>
        ),
    };

    const printOption: WithMenuOption = {
        label: "Export as PDF",
        onClick: () => {
            if (printDocument) printDocument();
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Printer strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                {"Export as PDF"}
            </FlexRowAlignCenter>
        ),
    };

    const editTagsOption: WithMenuOption = {
        label: "Edit tags",
        onClick: () =>
            openTaggingPopup({
                note: note as Note,
                onSave: async newTags => {
                    renameKey(newTags, "exam", "exam_v2");
                    renameKey(newTags, "custom", "tags");
                    await update(newTags);

                    closeTaggingPopup();
                },
            }),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Tag strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Edit tags
            </FlexRowAlignCenter>
        ),
        hide: readOnly,
    };

    const moveToOption: WithMenuOption = {
        label: "Move to",
        onClick: async () => {
            if (!note) return;

            openMoveToPopup({
                onMove: async ({ classId: destinationClassId, folderId: destinationFolderId, sharedSections }) => {
                    moveNotes({
                        noteIds: [{ userId: note.userId, noteId }],
                        sourceFolderId: note.folderId ?? null,
                        destinationFolderId,
                        sourceClassId: note.classId ?? null,
                        destinationClassId,
                        sharedSections,
                        ...(destinationClassId ? { isPublic: false } : {}),
                    });
                },
                selectedFolders: {},
                currentClassIdOfItem: note.classId ?? null,
                currentFolderIdOfItem: note.folderId ?? null,
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <FolderInput strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> {"Move to"}
            </FlexRowAlignCenter>
        ),
        hide: readOnly && courseReadOnly,
    };

    const trashOption: WithMenuOption = {
        label: note?.classId ? "Remove from class" : "Trash this note",
        onClick: handleTrashClick,
        menuItemProps: {
            sx: {
                borderTop: `2px solid ${themeColors.neutral1}`,
                height: "4.7rem",
                color: themeColors.errorPrimary,
                fontWeight: "600",
                fontSize: "1.5rem",
            },
        },
        hide: readOnly && courseReadOnly,
    };

    const restoreOption: WithMenuOption = {
        label: "Restore",
        onClick: async () => {
            restoreNotes({ noteIds: [{ userId: note?.userId, noteId }], sourceFolderId: null }).then(() => {
                toast.success("Note restored!");
            });
        },
        hide: readOnly || Boolean(!note?.trash),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Undo strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Restore
            </FlexRowAlignCenter>
        ),
    };

    const deletePermanentOption: WithMenuOption = {
        label: "Delete",
        onClick: deletePermanently,
        hide: readOnly || Boolean(!note?.trash),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Trash strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Delete
            </FlexRowAlignCenter>
        ),
    };

    const renameOption: WithMenuOption = {
        label: "Rename",
        onClick: async () => setIsRenamePopupOpen(true),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <PencilLine strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Rename
            </FlexRowAlignCenter>
        ),
        hide: readOnly,
    };

    const SharingIcon = note?.public ? Globe : note?.password ? KeyRound : Lock;

    const manageSharingOption: WithMenuOption = {
        label: "Manage sharing",
        onClick: () => {
            openContentItemsSharingPopup({
                notes: note ? [note] : undefined,
                itemType: ItemType.NOTE,
            });
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <SharingIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Manage sharing
            </FlexRowAlignCenter>
        ),
    };

    const getLinkOption: WithMenuOption = {
        label: "Get link",
        onClick: () => {
            if (!readOnly && !isPasswordProtected && !isClassItem && !isPublic) {
                setPublicAndGetLink(!isPublic);
            } else {
                copyToClipboard(encodeURI(window.location.origin) + noteRoute);
            }
        },
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <Link
                    strokeWidth={strokeWidth.normal}
                    size={iconSizes.SM}
                    data-testid="auto-id-2cf5ba56-2330-40e0-baf2-6be6ecd0fb92"
                />
                {"Get link"}
            </FlexRowAlignCenter>
        ),
        hide: !sharingEnabled && user?.ID === note?.userId,
    };

    const openInNewTabOption: WithMenuOption = {
        label: "Open in new tab",
        onClick: () => window.open(window.location.origin + noteRoute, "_blank"),
        node: (
            <FlexRowAlignCenter style={{ gap: "0.9rem" }}>
                <ExternalLink strokeWidth={strokeWidth.normal} size={iconSizes.SM} /> Open in new tab
            </FlexRowAlignCenter>
        ),
    };

    const noteCardOptions: WithMenuOption[] = note?.trash
        ? [restoreOption, deletePermanentOption]
        : isClassItem
          ? courseReadOnly
              ? [notAnOwnerAlert, getLinkOption, openInNewTabOption]
              : [
                    readOnly ? notAnOwnerAlert : null,
                    !readOnly ? duplicateOption : null,
                    moveToOption,
                    !readOnly ? renameOption : null,
                    manageSharingOption,
                    getLinkOption,
                    openInNewTabOption,
                    !readOnly ? editTagsOption : null,
                    trashOption,
                ]
          : [
                saveOption,
                duplicateOption,
                moveToOption,
                renameOption,
                getLinkOption,
                openInNewTabOption,
                editTagsOption,
                !readOnly ? manageSharingOption : null,
                trashOption,
            ];

    const notePageOptions: WithMenuOption[] = note?.trash
        ? [restoreOption, deletePermanentOption]
        : [
              copilotSettingsOption,
              !readOnly ? manageSharingOption : null,
              saveOption,
              duplicateOption,
              printOption,
              editTagsOption,
              moveToOption,
              trashOption,
          ];

    const options = isNotePageMenu ? notePageOptions : noteCardOptions;

    return (
        <>
            <WithOptionsMenu
                options={options}
                menuItemProps={{ sx: { padding: "1rem 1.6rem" } }}
                menuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: "2rem",
                            width: "20.6rem",
                            fontSize: "1.4rem",
                            margin: "-0.5rem 0 0 -1.25rem",
                        },
                    },
                    transformOrigin: { vertical: "top", horizontal: isNotePageMenu ? "right" : "left" },
                }}>
                {({ openMenu }) => (
                    <ThreeDotsBtn
                        itemId={noteId}
                        onPointerEnter={() => setShouldHookLoad(true)}
                        onClick={e => {
                            openMenu(e);
                            setShouldHookLoad(true);
                        }}
                        sx={btnSx}
                    />
                )}
            </WithOptionsMenu>
            {/* The print function require to have its content displayed */}
            <Portal>
                <Mountable mount={isNotePageMenu}>
                    <div style={{ display: "none" }} data-testid="auto-id-56799260-6c2d-4c34-bdcf-ef7a29be61e0">
                        <NoteToPrint key={String(isNotePageMenu)} />
                    </div>
                </Mountable>
            </Portal>
            <Portal>
                <LazyLoaded load={isRenamePopupOpen}>
                    {/* disable propagation since we don't want to trigger note anchor href when inside the popup */}
                    <div onClick={e => e.stopPropagation()} data-testid="auto-id-d0a76d07-affe-4ee0-9329-36835e863050">
                        <NoteNamingPopup
                            defaultName={note?.title as string}
                            isOpen={isRenamePopupOpen}
                            onClose={() => setIsRenamePopupOpen(false)}
                            onConfirm={async newNoteTitle => {
                                await update({ title: newNoteTitle });
                                toast.success("Note renamed");
                            }}
                        />
                    </div>
                </LazyLoaded>

                <LazyLoaded load={isCopilotSettingsOpen}>
                    <div onClick={e => e.stopPropagation()} data-testid="auto-id-46bc956f-5a2d-4b2c-875c-06b9cc58694d">
                        <CopilotSettingsPopup
                            isOpen={isCopilotSettingsOpen}
                            onClose={() => setIsCopilotSettingsOpen(false)}
                            noteId={noteId}
                        />
                    </div>
                </LazyLoaded>
            </Portal>
        </>
    );
};

export default NoteOptionsMenu;
